import { observer } from 'mobx-react-lite'
import { ResponseValueContainer } from '@/features/jobs/questions/components/ResponseValueContainer'
import { match } from '@/utils/pattern-match-util'
import { EmptyState, EmptyStateDescription } from '@/components/EmptyState'
import { type QuestionListViewModel } from '@/features/jobs/questions/QuestionListViewModel'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { BusinessInfoContainer } from '@/features/jobs/info-gathering/business-info/components/BusinessInfo/BusinessInfoContainer'
import { PersonalProfileContainer } from '@/features/jobs/info-gathering/individual-info/components/PersonalProfile/PersonalProfileContainer'
import { SpousalProfileContainer } from '@/features/jobs/info-gathering/individual-info/components/SpousalProfile/SpousalProfileContainer'
import { DependentsProfileContainer } from '@/features/jobs/info-gathering/individual-info/components/DependentsProfile/DependentsProfileContainer'
import { RefundInfoContainer } from '@/features/jobs/info-gathering/tax-refund-method/components/RefundInfo/RefundInfoContainer'
import {
  CapturedInformationCard,
  CapturedInformationQuestion,
  CapturedInformationQuestionList,
} from '@/features/jobs/details/components/CapturedInformationCard'
import { CapturedInformationLoadContainer } from '@/features/jobs/details/components/CapturedInformationLoadContainer'
import { NotesTabContainer } from './NotesTabContainer'

export interface CapturedInformationCardContainerProps {
  vm: JobDetailsViewModel
}

export const CapturedInformationCardContainer = observer(
  function CapturedInformationCardContainer({
    vm,
  }: CapturedInformationCardContainerProps) {
    const renderQuestions = () =>
      vm.detailsVm.responses.questionVms.length > 0 ||
      vm.job.answers.length > 0 ? (
        <QuestionsTabContainer vm={vm.detailsVm.responses} />
      ) : (
        <EmptyState size="card" padTop>
          <EmptyStateDescription>Nothing to display.</EmptyStateDescription>
        </EmptyState>
      )

    return (
      <CapturedInformationCard
        showEditButton={vm.showDraftView}
        onEdit={vm.actionsVm.editDraft}
        tabs={vm.capturedInformationVm.capturedInformationTabs}
        activeTab={vm.detailsVm.capturedInformationTabController.current.tab}
        onTabChange={vm.detailsVm.capturedInformationTabController.change}
      >
        {match(vm.detailsVm.capturedInformationTabController.current.tab, {
          Questions: () =>
            vm.detailsVm.fetchResponses.match({
              pending: () =>
                vm.detailsVm.isRevalidatingResponses ? (
                  renderQuestions()
                ) : (
                  <ContentLoading size="section" />
                ),
              rejected: (err) => (
                <ContentError
                  size="section"
                  title="Error loading questions."
                  error={err}
                />
              ),
              resolved: renderQuestions,
            }),
          Notes: () => (
            <CapturedInformationLoadContainer vm={vm.capturedInformationVm}>
              <NotesTabContainer vm={vm.capturedInformationVm.notesVm} />
            </CapturedInformationLoadContainer>
          ),
          BusinessInfo: () => (
            <CapturedInformationLoadContainer vm={vm.capturedInformationVm}>
              {vm.capturedInformationVm.businessInfoVm && (
                <BusinessInfoContainer
                  vm={vm.capturedInformationVm.businessInfoVm}
                  editVm={vm.capturedInformationVm.editBusinessProfileDialogVm}
                />
              )}
            </CapturedInformationLoadContainer>
          ),
          PersonalInfo: () => (
            <CapturedInformationLoadContainer vm={vm.capturedInformationVm}>
              {vm.capturedInformationVm.personalProfileVm && (
                <PersonalProfileContainer
                  vm={vm.capturedInformationVm.personalProfileVm}
                  editVm={vm.capturedInformationVm.editPersonalProfileDialogVm}
                />
              )}
              {vm.capturedInformationVm.spousalProfileVm && (
                <SpousalProfileContainer
                  vm={vm.capturedInformationVm.spousalProfileVm}
                  editVm={vm.capturedInformationVm.editPersonalProfileDialogVm}
                />
              )}
              {vm.capturedInformationVm.dependentProfileVm && (
                <DependentsProfileContainer
                  vm={vm.capturedInformationVm.dependentProfileVm}
                  editVm={vm.capturedInformationVm.editPersonalProfileDialogVm}
                />
              )}
            </CapturedInformationLoadContainer>
          ),
          RefundInfo: () => (
            <CapturedInformationLoadContainer vm={vm.capturedInformationVm}>
              {vm.capturedInformationVm.taxRefundMethodVm && (
                <RefundInfoContainer
                  vm={vm.capturedInformationVm.taxRefundMethodVm}
                  editVm={vm.capturedInformationVm.editRefundDialogVm}
                />
              )}
            </CapturedInformationLoadContainer>
          ),
        })}
      </CapturedInformationCard>
    )
  },
)

interface QuestionsTabContainerProps {
  vm: QuestionListViewModel
}

const QuestionsTabContainer = observer(function QuestionsTabContainer({
  vm,
}: QuestionsTabContainerProps) {
  return (
    <CapturedInformationQuestionList>
      {vm.questionVms.map((questionVm) => (
        <CapturedInformationQuestion
          key={questionVm.id}
          question={questionVm.title}
        >
          <ResponseValueContainer editorVm={questionVm.editor} />
        </CapturedInformationQuestion>
      ))}
    </CapturedInformationQuestionList>
  )
})
